import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import IvyVine from "@/components/IvyVine";
import { FlowerPink, FlowerRed, FlowerBlue, FlowerYellow, FlowerLavender } from "@/components/FlowerDeco";

const pillars = [
  {
    key: "planters",
    eyebrow: "Floral Planters",
    headline: "Beautiful. Fresh. Inspired.",
    body: "Beautifully designed planters to inspire and uplift any space.",
    image: "/assets/products/planter_medium.jpg",
    to: "/shop/planters",
    testid: "pillar-planters",
  },
  {
    key: "candles",
    eyebrow: "Scented Candles",
    headline: "Relax. Unwind. Recharge.",
    body: "Clean, long-lasting candles that create peace, comfort, and beautiful aromas.",
    image: "/assets/products/candle_8oz.jpg",
    to: "/shop/candles",
    testid: "pillar-candles",
  },
  {
    key: "oils",
    eyebrow: "Hair Growth Oils",
    headline: "Nourish. Strengthen. Grow.",
    body: "Nutrient-rich oils to nourish the scalp, strengthen roots, and promote healthy growth.",
    image: "/assets/hair_oils.jpg",
    to: "/shop/oils",
    testid: "pillar-oils",
  },
];

export default function ThreePillars() {
  return (
    <section
      data-testid="three-pillars-section"
      id="pillars"
      className="relative bg-brand-parchment py-24 lg:py-32 overflow-hidden"
    >
      {/* Brand watercolor flowers in corners */}
      <img src="/assets/flower_bouquet.png" alt="" aria-hidden="true" className="absolute -top-8 left-0 w-60 lg:w-72 opacity-90 animate-float-slow pointer-events-none" />
      <img src="/assets/flower_fern.png" alt="" aria-hidden="true" className="absolute -bottom-4 -right-4 w-60 lg:w-80 opacity-90 animate-drift pointer-events-none" />

      {/* Garden frame of colorful flowers */}
      <FlowerPink className="absolute -top-4 left-[34%] w-16 h-16 opacity-90 animate-float-slow" />
      <FlowerYellow className="absolute top-10 left-[18%] w-12 h-12 opacity-95 animate-drift" />
      <FlowerBlue className="absolute -top-2 right-[24%] w-14 h-14 opacity-90 animate-float-slow" style={{ animationDelay: "0.5s" }} />
      <FlowerLavender className="absolute -bottom-2 left-[42%] w-12 h-20 opacity-95" />
      <FlowerRed className="absolute bottom-16 left-[6%] w-14 h-14 opacity-90 animate-drift" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.35em] text-[#B7574E]">
            What We Make
          </span>
          <h2 className="mt-3 font-serif text-4xl lg:text-5xl text-brand-moss leading-tight">
            Three small rituals.
            <br />
            <span className="italic">One quiet life.</span>
          </h2>
          <div className="mt-5 flex justify-center items-center gap-3 text-brand-gold">
            <span className="h-px w-16 bg-brand-gold/40" />
            <Heart className="w-4 h-4 fill-current" />
            <span className="h-px w-16 bg-brand-gold/40" />
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {pillars.map((p, idx) => (
            <article
              key={p.key}
              data-testid={p.testid}
              className="group relative bg-brand-cream rounded-3xl overflow-hidden border border-brand-moss/15 hover:-translate-y-1.5 hover:shadow-xl transition-all duration-500"
              style={{ animationDelay: `${idx * 120}ms` }}
            >
              <IvyVine
                className="absolute -top-3 -left-3 w-28 opacity-70 z-10 pointer-events-none"
                flip={idx % 2 === 1}
              />
              <IvyVine
                className="absolute -bottom-3 -right-3 w-28 opacity-50 z-10 pointer-events-none rotate-180"
              />

              <div
                className="aspect-[4/5] w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${p.image})` }}
              />

              <div className="p-7 relative">
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#B7574E]">
                  {p.eyebrow}
                </span>
                <h3 className="mt-3 font-serif text-2xl text-brand-moss leading-tight">
                  {p.headline}
                </h3>
                <p className="mt-3 text-sm text-brand-moss/70 leading-relaxed">
                  {p.body}
                </p>
                <Link
                  to={p.to}
                  data-testid={`${p.testid}-cta`}
                  className="mt-5 inline-flex items-center gap-2 text-sm text-brand-moss border-b border-brand-gold pb-1 hover:gap-3 transition-all"
                >
                  View Collection
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-16 text-center text-sm uppercase tracking-[0.4em] text-brand-moss/70">
          Faith <span className="text-brand-gold mx-3">•</span> Purpose <span className="text-brand-gold mx-3">•</span> Wellness <span className="text-brand-gold mx-3">•</span> Beauty <span className="text-brand-gold ml-2">♡</span>
        </p>
      </div>
    </section>
  );
}
